import React, { useCallback, useEffect, useState, useContext } from "react";
import { Tooltip } from "antd";

// api
import { createCarouselStep1, createTemplateStep1 } from "../../api/Api";

// utils
import { useNavigate } from "react-router-dom";

import NotificationContext from "../../../../providers/NotificationProvider";
import { showErrorToast } from "../../../../../src/components/commonComponents/gxAntdToast/toastUtils";

// assets
import Bell from "../../../../assets/customSVG/Bell";
import Key from "../../../../assets/customSVG/Key";
import SpeakerPhone from "../../../../assets/customSVG/SpeakerPhone";

// hooks
import useToggle from "../../../../hooks/useToggle";

// Feature Gating
import { useFunction } from "../../../../hooks/useFeatureGating";
import { FUNCTIONS } from "../../../../constant/featureSlugs";

// constants
import {
  authenticationTypes,
  LANGUAGE_OPTIONS,
  marketingTypes,
  utilityTypes,
} from "../../constant";
import {
  FEATURE_FLAG_AUTHENTICATION_TEMPLATE,
  FEATURE_FLAG_CAROUSEL_TEMPLATE,
} from "../../constant/featureFlags";

// redux
import {
  resetTemplateDetails,
  update,
  updateLanguage,
  updateTemplateDetails,
} from "../../../../reduxToolkit/templateSlice";
import {
  useAspSelector,
  useAspDispatch,
} from "../../../../test/jest-redux-hooks";

// helpers
import { cn } from "../../../../helper/cn";
import {
  getLanguageCode,
  getLanguageName,
} from "../../../../helper/setLanguageCode";

// components
import RadioOption from "../../../../components/form/RadioButton/radioOption";
import TemplateName from "./TemplateName";
import Select from "../../../../components/commonComponents/select/index";
import UpgradeBadge from "../../../../components/commonComponents/UpgradeBadge/UpgradeBadge";

const CreateTemplateWA = () => {
  const templateDetails = useAspSelector((state) => state?.Template);
  const { currentBrand, featureFlags } = useAspSelector((state) => state.app);

  const navigate = useNavigate();
  const dispatch = useAspDispatch();
  const notification = useContext(NotificationContext);
  const isWhatsappPaymentEnabled = featureFlags?.whatsapp_payment_feature;

  // Feature Gating: Check permissions for each template type
  const { isEnabled: canCreateMarketingCustom } = useFunction(
    FUNCTIONS.TEMPLATE_MARKETING_CUSTOM
  );
  const { isEnabled: canCreateMarketingCarousel } = useFunction(
    FUNCTIONS.TEMPLATE_MARKETING_CAROUSEL
  );
  const { isEnabled: canCreateMarketingOrder } = useFunction(
    FUNCTIONS.TEMPLATE_MARKETING_ORDER
  );
  const { isEnabled: canCreateUtilityCustom } = useFunction(
    FUNCTIONS.TEMPLATE_UTILITY_CUSTOM
  );
  const { isEnabled: canCreateUtilityOrder } = useFunction(
    FUNCTIONS.TEMPLATE_UTILITY_ORDER
  );
  const { isEnabled: canCreateAuthCode } = useFunction(
    FUNCTIONS.TEMPLATE_AUTH_CODE
  );

  const [data, setData] = useState(null);
  const [language, setLanguage] = useState(null);
  const [templateNameError, setTemplateNameError] = useToggle(false);
  const [checkingtemplateName, setCheckingtemplateName] = useToggle(false);

  useEffect(() => {
    const language = templateDetails?.language?.[0]
      ? Object?.keys(templateDetails?.language?.[0])
      : "";

    if (language?.length > 0)
      setLanguage({
        label: language?.[0],
        value: language?.[0],
      });
    else setLanguage(null);
  }, []);

  useEffect(() => {
    setData(templateDetails);
  }, [templateDetails]);

  // if all the required deatils are filled allow to continue to template details page
  const handleContinue = useCallback(
    async (e) => {
      if (
        !templateNameError &&
        data?.category &&
        data?.categoryType &&
        data?.name &&
        language?.value?.length > 0 &&
        !checkingtemplateName
      ) {
        if (
          data?.continueDetails === null ||
          data?.continueDetails?.template?.name !== data?.name
        ) {
          const res = await step1Carousel();
          if (res?.status === 200) {
            const { id } = res?.data?.template;
            next({
              id: id,
              name: data?.name,
              slug: res?.data?.template?.slug,
              category: data?.category,
              language: getLanguageCode(language?.value),
              subcategory_type: data?.categoryType,
            });
          } else {
            showErrorToast(notification, res?.response?.data?.error || "Failed to create template");
          }
        } else {
          navigate("/user/channels/whatsapp/create-template/template-details");
        }
      }
      return;
    },

    [data, templateNameError, checkingtemplateName]
  );

  const next = (data) => {
    dispatch(updateTemplateDetails({ data: data }));
    navigate("/user/channels/whatsapp/create-template/template-details");
  };

  // update categorys
  const updateCategory = (type, subType) => {
    dispatch(
      updateTemplateDetails({
        data: {
          category: type,
          brand_id: currentBrand?.brand_id,
          name: data?.name,
          language: getLanguageCode(language?.value),
          subcategory_type: subType?.toUpperCase(),
        },
      })
    );

    // state.details = data;
    // state.category = data.category;
    // state.categoryType = data?.subcategory_type?.toUpperCase();
    // state.name = data.name;
    // state.language = [data.language];
    // state.id = data?.id;
  };

  // discard the template details
  const discard = () => {
    dispatch(resetTemplateDetails());
    navigate("/user/channels/whatsapp/message-templates");
  };

  const step1Carousel = async () => {
    const payLoad = {
      category: data?.category,
      brand_id: currentBrand?.brand_id,
      name: data?.name,
      language: getLanguageCode(language?.value),
      subcategory_type: data?.categoryType,
    };

    let res;

    if (data?.categoryType === "CAROUSEL") {
      res = await createCarouselStep1(payLoad);
    } else {
      res = await createTemplateStep1(payLoad);
    }

    return res;
  };

  // styles
  const header = "font-normal weight-bold mb-[0.3vw] text-[var(--contentText)]";
  const subHeader =
    "weight-medium text-[var(--font-400)] pt-[0.03vw] pb-[0.7vw]";
  const title = "font-md weight-bold text-[var(--contentText)]";
  const subTitle = "font-sm weight-medium text-[var(--font-400)] mt-[0.25vw]";
  const marketing = "flex gap-[0.3vw] ml-[3.1vw]";

  return (
    <div className="h-[calc(100vh-48px)] flex flex-col overflow-hidden">
      <div className="flex flex-col flex-1 overflow-hidden">
        {/* Fixed Header */}
        <div className="flex-shrink-0">
          <div className="w-auto flex items-center justify-between bg-[var(--white)] mx-[0.7vw] mt-[0.7vw] px-[1vw] py-[1vw] rounded-t-[0.8vw]">
            <div className="font-larger weight-bold text-[var(--contentText)]">
              New message template
            </div>
            <div className="w-auto flex items-center gap-[1vw]">
              <button
                className="font-md px-[0.6vw] py-[0.37vw] weight-medium border-0 outline-none tracking-[0.02vw] text-[var(--channelToggleSwitch)] rounded-[0.37vw] hover:bg-slate-100"
                onClick={() => {
                  discard();
                }}
              >
                Discard
              </button>

              <button
                type="submit"
                className={`font-md px-[0.6vw] py-[0.37vw] rounded-[0.37vw] weight-medium border-0 outline-none tracking-[0.02vw] ${
                  !templateNameError &&
                  data?.category &&
                  data?.name &&
                  language?.value?.length > 0
                    ? "!bg-[var(--new-primary)] !text-[var(--appHeaderBG)] cursor-pointer"
                    : "!bg-[var(--lightGrey)] !text-[var(--darkgrey)] cursor-not-allowed"
                }`}
                onClick={(e) => {
                  handleContinue(e);
                }}
              >
                Continue
              </button>
            </div>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="pt-[12px] px-[130px] flex flex-col gap-[0.5vw] pb-4 min-h-0">
            <div className="w-full bg-[var(--appHeaderBG)] px-[1vw] pt-[0.5vw] pb-[1vw]">
              <div className="border-b-[0.08vw] border-[var(--border-50)]">
                <h2 className={`${header}`}>Category</h2>
                <p className={`${subHeader} font-md`}>
                  Choose a category that best describes your message template.
                </p>
              </div>
              <div className="flex flex-col gap-[1vw] mt-[0.5vw]">
                {/* For Marketing Template */}
                <div
                  className={`w-full px-[0.8vw] py-[0.6vw] rounded-[0.8vw] ${
                      (data?.category === 'MARKETING' && marketingTypes.includes(data?.categoryType?.toLowerCase()))
                      ? "!bg-[var(--primaryBackground)]"
                      : "!bg-[var(--channelButton)]"
                  }`}
                >
                  <div className="flex-row align-center gap-[0.8vw]">
                    <div
                      className={`w-[2.5vw] h-[2.5vw] ${
                        (data?.category === 'MARKETING' && marketingTypes.includes(data?.categoryType?.toLowerCase()))
                          ? "bg-[#25c277]"
                          : "bg-white"
                      } rounded-full flex items-center justify-center`}
                    >
                      <SpeakerPhone
                        width={"1.6vw"}
                        height={"1.6vw"}
                        stroke={
                          (data?.category === 'MARKETING' && marketingTypes.includes(data?.categoryType?.toLowerCase()))
                            ? "#fff"
                            : "#616874"
                        }
                      />
                    </div>
                    <div className="flex flex-col">
                      <h3 className={`${header}`}>Marketing</h3>
                      <p className={`${subHeader} font-sm`}>
                        Promotions or information about your business, products or
                        services Or any message that isn't utility or
                        authentication.
                      </p>
                    </div>
                  </div>

                  {/* Custom Template */}
                  <div
                    className={cn(
                      marketing,
                      "mt-[0.5vw]",
                      canCreateMarketingCustom ? "cursor-pointer" : "cursor-not-allowed"
                    )}
                    onClick={() => {
                      if (canCreateMarketingCustom) {
                        updateCategory("MARKETING", "custom");
                      }
                    }}
                  >
                    <div className="flex gap-[0.3vw]">
                      <div style={{ opacity: !canCreateMarketingCustom ? 0.5 : 1 }}>
                        <RadioOption
                          value="marketing_custom"
                          selectedValue={data?.categoryType?.toLowerCase() === 'custom' && data?.category === 'MARKETING' ? 'marketing_custom' : data?.categoryType?.toLowerCase()}
                          id="marketing_custom"
                          name="template_category"
                          onChange={() => {
                            if (canCreateMarketingCustom) {
                              updateCategory("MARKETING", "custom");
                            }
                          }}
                          disabled={!canCreateMarketingCustom}
                          style={{
                            width: "1.2vw",
                            height: "1.2vw",
                            margin: "0.15vw 0 0",
                          }}
                        />
                      </div>
                      <label 
                        htmlFor="marketing_custom" 
                        className={cn(canCreateMarketingCustom ? "cursor-pointer" : "cursor-not-allowed")}
                      >
                        <div className="flex items-center gap-[0.3vw]">
                          <h3 className={`${title}`} style={{ opacity: !canCreateMarketingCustom ? 0.5 : 1 }}>Custom</h3>
                          {!canCreateMarketingCustom && <UpgradeBadge iconOnly={false} />}
                        </div>
                        <p className={`${subTitle} font-sm`} style={{ opacity: !canCreateMarketingCustom ? 0.5 : 1 }}>
                          Send promotional offers, announcements and more to
                          increase awareness and engagement.
                        </p>
                      </label>
                    </div>
                  </div>

                  {/* Carousel template */}
                  <div
                    className={cn(
                      "mt-[0.7vw]",
                      marketing,
                      canCreateMarketingCarousel
                        ? "cursor-pointer"
                        : "cursor-not-allowed"
                    )}
                    onClick={() => {
                      if (canCreateMarketingCarousel) {
                        updateCategory("MARKETING", "carousel");
                      }
                    }}
                  >
                    <div className="flex gap-[0.3vw]">
                      <div style={{ opacity: !canCreateMarketingCarousel ? 0.5 : 1 }}>
                        <RadioOption
                          value="carousel"
                          selectedValue={data?.categoryType?.toLowerCase()}
                          onChange={() => {
                            if (canCreateMarketingCarousel) {
                              updateCategory("MARKETING", "carousel");
                            }
                          }}
                          id="carousel"
                          name="carousel"
                          disabled={!canCreateMarketingCarousel}
                          className={cn(
                            canCreateMarketingCarousel
                              ? "!cursor-pointer"
                              : "!cursor-not-allowed"
                          )}
                          style={{
                            width: "1.2vw",
                            height: "1.2vw",
                            margin: "0.15vw 0 0",
                          }}
                        />
                      </div>
                      <label
                        htmlFor="carousel"
                        className={cn(
                          canCreateMarketingCarousel
                            ? "cursor-pointer"
                            : "cursor-not-allowed"
                        )}
                      >
                        <div className="flex items-center gap-[0.3vw]">
                          <h3 className={`${title}`} style={{ opacity: !canCreateMarketingCarousel ? 0.5 : 1 }}>Carousel</h3>
                          {!canCreateMarketingCarousel && <UpgradeBadge iconOnly={false} />}
                        </div>
                        <p className={`${subTitle} font-sm`} style={{ opacity: !canCreateMarketingCarousel ? 0.5 : 1 }}>
                          Increase your brand visibility, reach and Engage your
                          audience with eye-catching carousels.
                        </p>
                      </label>
                    </div>
                  </div>

                  {/* Order Detail Template */}
                  <div
                    className={cn(
                      "mt-[0.7vw]",
                      marketing,
                      canCreateMarketingOrder
                        ? "cursor-pointer"
                        : "cursor-not-allowed"
                    )}
                    onClick={() => {
                      if (canCreateMarketingOrder) {
                        updateCategory("MARKETING", "order_detail");
                      }
                    }}
                  >
                    <div className="flex gap-[0.3vw]">
                      <div style={{ opacity: !canCreateMarketingOrder ? 0.5 : 1 }}>
                        <RadioOption
                          value="marketing_order_detail"
                          selectedValue={data?.categoryType?.toLowerCase() === 'order_detail' && data?.category === 'MARKETING' ? 'marketing_order_detail' : data?.categoryType?.toLowerCase()}
                          onChange={() => {
                            if (canCreateMarketingOrder) {
                              updateCategory("MARKETING", "order_detail");
                            }
                          }}
                          id="marketing_order_detail"
                          name="marketing_category"
                          disabled={!canCreateMarketingOrder}
                          className={cn(
                            canCreateMarketingOrder ? "!cursor-pointer" : "!cursor-not-allowed"
                          )}
                          style={{
                            width: "1.2vw",
                            height: "1.2vw",
                            margin: "0.15vw 0 0",
                          }}
                        />
                      </div>
                      <label
                        htmlFor="marketing_order_detail"
                        className={cn(
                          canCreateMarketingOrder
                            ? "cursor-pointer"
                            : "cursor-not-allowed"
                        )}
                      >
                        <div className="flex items-center gap-[0.3vw]">
                          <h3 className={`${title}`} style={{ opacity: !canCreateMarketingOrder ? 0.5 : 1 }}>Order Detail</h3>
                          {!canCreateMarketingOrder && <UpgradeBadge iconOnly={false} />}
                        </div>
                        <p className={`${subTitle} font-sm`} style={{ opacity: !canCreateMarketingOrder ? 0.5 : 1 }}>
                          Send messages through which customers can pay you.
                        </p>
                      </label>
                    </div>
                  </div>
                </div>

                {/* For Utility Template */}
                <div
                  className={`w-full px-[0.8vw] py-[0.6vw] rounded-[0.8vw] ${
                    (data?.category === 'UTILITY' && utilityTypes.includes(data?.categoryType?.toLowerCase()))
                      ? "!bg-[var(--primaryBackground)]"
                      : "!bg-[var(--channelButton)]"
                  }`}
                >
                  <div className="flex items-center gap-[0.8vw]">
                    <div
                      className={`w-[2.5vw] h-[2.5vw] flex items-center justify-center rounded-full ${
                        (data?.category === 'UTILITY' && utilityTypes.includes(data?.categoryType?.toLowerCase()))
                          ? "bg-[#25c277]"
                          : "bg-white"
                      }`}
                    >
                      <Bell
                        width={"1.6vw"}
                        height={"1.6vw"}
                        color={
                          (data?.category === 'UTILITY' && utilityTypes.includes(data?.categoryType?.toLowerCase()))
                            ? "#fff"
                            : "#616874"
                        }
                      />
                    </div>
                    <div className="flex flex-col">
                      <h2 className={`${header}`}>Utility</h2>
                      <p className={`${subHeader} font-sm`}>
                        Messages about a specific transaction, account, order or
                        customer request.
                      </p>
                    </div>
                  </div>

                  {/* Custom Option */}
                  <div
                    className={cn(
                      "mt-[0.7vw]",
                      marketing,
                      canCreateUtilityCustom ? "cursor-pointer" : "cursor-not-allowed"
                    )}
                    onClick={() => {
                      if (canCreateUtilityCustom) {
                        updateCategory("UTILITY", "custom");
                      }
                    }}
                  >
                    <div className="flex gap-[0.3vw]">
                      <div style={{ opacity: !canCreateUtilityCustom ? 0.5 : 1 }}>
                        <RadioOption
                          value="utility_custom"
                          selectedValue={data?.categoryType?.toLowerCase() === 'custom' && data?.category === 'UTILITY' ? 'utility_custom' : data?.categoryType?.toLowerCase()}
                          onChange={() => {
                            if (canCreateUtilityCustom) {
                              updateCategory("UTILITY", "custom");
                            }
                          }}
                          id="utility_custom"
                          name="template_category"
                          disabled={!canCreateUtilityCustom}
                          className={cn(
                            canCreateUtilityCustom ? "!cursor-pointer" : "!cursor-not-allowed"
                          )}
                          style={{
                            width: "1.2vw",
                            height: "1.2vw",
                            margin: "0.15vw 0 0",
                          }}
                        />
                      </div>
                      <label
                        htmlFor="utility_custom"
                        className={cn(
                          canCreateUtilityCustom ? "cursor-pointer" : "cursor-not-allowed"
                        )}
                      >
                        <div className="flex items-center gap-[0.3vw]">
                          <h3 className={`${title}`} style={{ opacity: !canCreateUtilityCustom ? 0.5 : 1 }}>Custom</h3>
                          {!canCreateUtilityCustom && <UpgradeBadge iconOnly={false} />}
                        </div>
                        <p className={`${subTitle} font-sm`} style={{ opacity: !canCreateUtilityCustom ? 0.5 : 1 }}>
                          Send messages about an existing order or account.
                        </p>
                      </label>
                    </div>
                  </div>

                  {/* Order Detail Option */}
                  <div
                    className={cn(
                      "mt-[0.7vw]",
                      marketing,
                      canCreateUtilityOrder ? "cursor-pointer" : "cursor-not-allowed"
                    )}
                    onClick={() => {
                      if (canCreateUtilityOrder) {
                        updateCategory("UTILITY", "order_detail");
                      }
                    }}
                  >
                    <div className="flex gap-[0.3vw]">
                      <div style={{ opacity: !canCreateUtilityOrder ? 0.5 : 1 }}>
                        <RadioOption
                          value="utility_order_detail"
                          selectedValue={data?.categoryType?.toLowerCase() === 'order_detail' && data?.category === 'UTILITY' ? 'utility_order_detail' : data?.categoryType?.toLowerCase()}
                          onChange={() => {
                            if (canCreateUtilityOrder) {
                              updateCategory("UTILITY", "order_detail");
                            }
                          }}
                          id="utility_order_detail"
                          name="utility_category"
                          disabled={!canCreateUtilityOrder}
                          className={cn(
                            canCreateUtilityOrder ? "!cursor-pointer" : "!cursor-not-allowed"
                          )}
                          style={{
                            width: "1.2vw",
                            height: "1.2vw",
                            margin: "0.15vw 0 0",
                          }}
                        />
                      </div>
                      <label
                        htmlFor="utility_order_detail"
                        className={cn(
                          canCreateUtilityOrder ? "cursor-pointer" : "cursor-not-allowed"
                        )}
                      >
                        <div className="flex items-center gap-[0.3vw]">
                          <h3 className={`${title}`} style={{ opacity: !canCreateUtilityOrder ? 0.5 : 1 }}>Order Detail</h3>
                          {!canCreateUtilityOrder && <UpgradeBadge iconOnly={false} />}
                        </div>
                        <p className={`${subTitle} font-sm`} style={{ opacity: !canCreateUtilityOrder ? 0.5 : 1 }}>
                          Send messages through which customers can pay you.
                        </p>
                      </label>
                    </div>
                  </div>

                  {/* Order Status Option */}
                  {/* <div
                    className={cn(
                      "mt-[0.7vw] cursor-pointer",
                      marketing,
                      true ? "cursor-pointer" : "cursor-not-allowed"
                    )}
                    onClick={() => updateCategory("UTILITY", "order_status")}
                  >
                    <RadioOption
                      value="utility_order_status"
                      selectedValue={data?.categoryType?.toLowerCase() === 'order_status' && data?.category === 'UTILITY' ? 'utility_order_status' : data?.categoryType?.toLowerCase()}
                      onChange={() => updateCategory("UTILITY", "order_status")}
                      id="utility_order_status"
                      name="template_category"
                      className={cn(
                        true ? "!cursor-pointer" : "!cursor-not-allowed"
                      )}
                      style={{
                        width: "1.2vw",
                        height: "1.2vw",
                        margin: "0.15vw 0 0",
                      }}
                    />
                    <label
                      htmlFor="utility_order_status"
                      className={cn(
                        true ? "cursor-pointer" : "cursor-not-allowed"
                      )}
                    >
                      <h3 className={`${title}`}>Order Status</h3>
                      <p className={`${subTitle} font-sm`}>
                        Send messages to tell customers about the progress of their orders.
                      </p>
                    </label>
                  </div> */}
                </div>

                {/* For authentication template */}
                <div
                  className={cn(
                    "w-full px-[1.2vw] py-[0.8vw] rounded-[0.8vw] flex items-center gap-3",
                    canCreateAuthCode ? "cursor-pointer" : "cursor-not-allowed",
                    authenticationTypes.includes(
                      data?.categoryType?.toLowerCase()
                    )
                      ? "!bg-[var(--primaryBackground)]"
                      : "!bg-[var(--channelButton)]"
                  )}
                  onClick={() => {
                    if (canCreateAuthCode) {
                      updateCategory("AUTHENTICATION", "copy_code");
                    }
                  }}
                >
                  <div className="flex items-center gap-[1vw]">
                    <div
                      className={`w-[2.5vw] h-[2.5vw] flex items-center justify-center rounded-full ${
                        authenticationTypes.includes(
                          data?.categoryType?.toLowerCase()
                        )
                          ? "bg-[#25c277]"
                          : "bg-white"
                      }`}
                      style={{ opacity: !canCreateAuthCode ? 0.5 : 1 }}
                    >
                      <Key
                        width={"1.5vw"}
                        height={"1.5vw"}
                        color={
                          authenticationTypes.includes(
                            data?.categoryType?.toLowerCase()
                          )
                            ? "#fff"
                            : "#616874"
                        }
                      />
                    </div>
                    <div className="">
                      <div className="flex items-center gap-[0.3vw]">
                        <h2 className={`${header}`} style={{ opacity: !canCreateAuthCode ? 0.5 : 1 }}>Authentication</h2>
                        {!canCreateAuthCode && <UpgradeBadge iconOnly={false} />}
                      </div>
                      <p className={`${subHeader} font-sm !pb-0`} style={{ opacity: !canCreateAuthCode ? 0.5 : 1 }}>
                        Send codes to verify a transaction or login.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <TemplateName
              type={"create"}
              checking={checkingtemplateName}
              setChecking={setCheckingtemplateName}
              onChange={(val) => {
                setData({ ...data, name: val });
              }}
              error={templateNameError}
              onErrorChange={(value) => {
                setTemplateNameError(value);
              }}
            />
            <div className="w-full bg-[var(--appHeaderBG)] px-[1vw] pt-[0.5vw] pb-[1vw] flex-column">
              <div className={`${header}`}>Languages</div>
              <p className={`${subHeader} font-md`}>
                Choose languages for your message template. You can delete or add
                more languages later.
              </p>

              <Select
                value={language}
                // options={
                //   data?.categoryType !== "AUTHENTICATION"
                //     ? LANGUAGE_OPTIONS
                //     : [{ value: "English", label: "English" }]
                // }
                options={
                  data?.categoryType !== "AUTHENTICATION"
                    ? LANGUAGE_OPTIONS
                    : LANGUAGE_OPTIONS.filter((option) =>
                        ["English", "English (US)", "English (UK)"].includes(
                          option.value
                        )
                      )
                }
                placeholder={"Select languages"}
                rootClassName="h-[2.3vw]"
                optionsClassName="!max-w-[15vw]"
                cardStyle="!max-h-[1.5vw]"
                onChange={(val) => {
                  setLanguage(val);
                  dispatch(updateLanguage(val?.value));
                }}
                optionClick="selection"
                // optionClick="language-click"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateTemplateWA;
